import React, { useState, Fragment, useEffect, useCallback } from 'react';
import {
  getCoreRowModel,
  useReactTable,
  flexRender,
  ExpandedState,
  getExpandedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  SortingState,
  getSortedRowModel,
  Row,
  FilterFn,
} from '@tanstack/react-table';
import { Updater } from '@tanstack/react-query';
// eslint-disable-next-line import/no-extraneous-dependencies
import { TableOptions } from '@tanstack/table-core';
import { Icon, Pagination, Text, type TOption, SearchTextInput, Button, SelectInput } from '@intellecteu/common-ui';
import cx from 'classnames';
import { rankItem } from '@tanstack/match-sorter-utils';
import { PAGE_SIZE } from 'app/constants';
import { TableSkeleton } from 'ui';
import { ui } from 'ui/styles';
import type { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';

import s from './new/styles.module.scss';

export function useTableFilteringRows<T>() {
  const [filteredRows, setFilteredRows] = useState<Row<T>[]>([]);

  const onRowsFilteringChange = useCallback((data: Row<T>[]) => {
    setFilteredRows(data);
  }, []);

  return {
    filteredRows,
    onRowsFilteringChange,
  };
}

interface ReactTableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T>[];
  className?: string;
  wrapperClassName?: string;
  subRowComponent?: React.ReactElement;
  options?: Omit<TableOptions<T>, 'data' | 'columns' | 'getCoreRowModel'>;
  isPaginated?: boolean;
  isSearchable?: boolean;
  isSortable?: boolean;
  isFetching?: boolean;
  // searchField?: string;
  initialSorting?: SortingState;
  columnVisibility?: Record<string, boolean>;
  setColumnVisibility?: (updater: Updater<Record<string, boolean>, any>) => void;
  onRowsFilteringChange?: (data: Row<T>[]) => void;
  actionsComponent?: React.ReactElement;
  serverSidePagination?: {
    pageIndex: number;
    pageSize: number;
    hasNextPage: boolean;
    onPageIndexChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  paginationRef?: React.RefObject<HTMLDivElement | null>;
  /** When provided with onClientPaginationChange, use controlled client-side pagination (e.g. for persisted state). pageIndex is 1-based. */
  initialClientPagination?: { pageIndex: number; pageSize: number };
  /** Called when user changes page or page size (client-side only). pageIndex is 1-based. */
  onClientPaginationChange?: (pageIndex: number, pageSize: number) => void;
}

const pageSize = PAGE_SIZE;
const emptyHeight = 525;
const pageSizes = [5, 10, 20, 30, 40, 50];

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

export const Table = <T extends object>({
  data,
  columns,
  className,
  wrapperClassName,
  subRowComponent: SubRow,
  options,
  isPaginated,
  isSearchable,
  isSortable,
  initialSorting,
  // searchField = 'name',
  columnVisibility,
  setColumnVisibility,
  onRowsFilteringChange,
  isFetching,
  actionsComponent: Actions,
  serverSidePagination,
  paginationRef,
  initialClientPagination,
  onClientPaginationChange,
}: ReactTableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting || []);

  const isClientPaginationControlled = initialClientPagination != null && onClientPaginationChange != null;
  const [clientPagination, setClientPagination] = useState(() =>
    isClientPaginationControlled
      ? {
          pageIndex: initialClientPagination!.pageIndex - 1,
          pageSize: initialClientPagination!.pageSize,
        }
      : { pageIndex: 0, pageSize },
  );

  useEffect(() => {
    if (isClientPaginationControlled && initialClientPagination) {
      setClientPagination({
        pageIndex: initialClientPagination.pageIndex - 1,
        pageSize: initialClientPagination.pageSize,
      });
    }
  }, [initialClientPagination?.pageIndex, initialClientPagination?.pageSize, isClientPaginationControlled]);

  const isServerSide = Boolean(serverSidePagination);

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    state: {
      expanded,
      ...(isSearchable && { columnFilters }),
      ...(isSortable && { sorting }),
      ...(isPaginated && !isServerSide && isClientPaginationControlled && { pagination: clientPagination }),
      ...options?.state,
      columnVisibility: { ...columnVisibility },
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    ...(isPaginated &&
      !isServerSide &&
      isClientPaginationControlled && {
        onPaginationChange: (updater) => {
          setClientPagination((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : prev;
            onClientPaginationChange!(next.pageIndex + 1, next.pageSize);
            return next;
          });
        },
      }),
    ...(isPaginated && !isServerSide && { getPaginationRowModel: getPaginationRowModel() }),
    ...options,
    // debugTable: true,
  });

  useEffect(() => {
    if (isPaginated && !isServerSide && !isClientPaginationControlled) table.setPageSize(pageSize);
  }, [isPaginated, isServerSide, isClientPaginationControlled]);

  const { getState, setPageSize, getRowModel, getHeaderGroups, setPageIndex } = table;

  const pageIndex = isServerSide ? serverSidePagination!.pageIndex : Number(getState().pagination.pageIndex) + 1;
  const [totalItems, setTotalItems] = useState<number>(table.getCoreRowModel().rows.length);

  useEffect(() => {
    const { rows } = table.getFilteredRowModel();
    setTotalItems(rows.length);
    if (typeof onRowsFilteringChange !== 'undefined') onRowsFilteringChange(rows);
  }, [table.getFilteredRowModel().rows.length]);

  return (
    <div className={cx(ui.wrapper, wrapperClassName)}>
      {(isSearchable || Actions) && (
        <div className={ui.tableActions}>
          <div className={ui.tableActionsFilter}>
            {isSearchable && (
              <SearchTextInput
                placeholder="Search by name"
                className={s.search}
                onClear={() => table.setGlobalFilter('')}
                onSearchClick={(value: string) => table.setGlobalFilter(value)}
                disabled={isFetching}
                // onClear={(value) => getColumn(searchField)?.setFilterValue(value)}
                // onSearchClick={(value) => getColumn(searchField)?.setFilterValue(value)}
              />
            )}
          </div>
          {Actions}
        </div>
      )}

      {isFetching ? (
        <TableSkeleton
          /* eslint-disable-next-line no-nested-ternary */
          rows={isServerSide ? serverSidePagination!.pageSize : isPaginated ? getState().pagination.pageSize : 5}
          columns={3}
        />
      ) : (
        <>
          <table className={cx(ui.table, className)}>
            <thead className={ui.tableHead}>
              {getHeaderGroups().map((headerGroup, trIndex) => (
                <tr key={`${headerGroup.id}_thead_tr_${trIndex}`}>
                  {headerGroup.headers.map((header, thIndex) => (
                    <th
                      key={`${header.id}_thead_th_${thIndex}`}
                      className={ui.tableHeadCell}
                      style={{
                        width: header.getSize() !== 0 ? header.getSize() : undefined,
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() && isSortable ? ui.cellWithSorting : ''}
                          onClick={header.column.getToggleSortingHandler()}
                          aria-hidden
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}

                          {header.column.getCanSort() && isSortable && (
                            <div className={ui.sortButtons}>
                              <Icon
                                icon={Icon.icons.chevronUp}
                                size="xxs"
                                className={
                                  (header.column.getIsSorted() as string) === 'asc' ? ui.sortIconActive : ui.sortIcon
                                }
                              />
                              <Icon
                                icon={Icon.icons.chevronDown}
                                size="xxs"
                                className={
                                  (header.column.getIsSorted() as string) === 'desc' ? ui.sortIconActive : ui.sortIcon
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={ui.tableBody}>
              {/* TBD, should we show next block in any case or just for paginated table */}
              {!table.getFilteredRowModel().rows.length && (
                <tr style={{ height: `${emptyHeight}px` }} className={ui.emptyBlock}>
                  <td colSpan={columns.length}>
                    <Text size="l" color={Text.color.darkBlue} component="span">
                      <span className={ui.emptyBlockMessage}>No records found by this filter</span>
                    </Text>
                  </td>
                </tr>
              )}
              {getRowModel().rows.map((row, trIndex) => (
                <Fragment key={`${row.id}_fragment_${trIndex}`}>
                  <tr key={`${row.id}_tr_${trIndex}`} className={ui.tableBodyRow}>
                    {row.getVisibleCells().map((cell, tdIndex) => (
                      <td
                        className={cx(ui.tableBodyCell, cell.column.columnDef.meta?.isLink && ui.tableBodyCellLink)}
                        key={`${cell.id}_td_${tdIndex}`}
                        style={{
                          width: cell.column.getSize() !== 0 ? cell.column.getSize() : undefined,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() ? (
                    <tr>
                      <td colSpan={3}>{SubRow}</td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>

          {isPaginated &&
            table.getFilteredRowModel().rows.length > 0 &&
            (isServerSide && serverSidePagination ? (
              <div className={s.paginationWrapper} ref={paginationRef}>
                <div className={s.pagination}>
                  <div className={s.paginationControls}>
                    <Button
                      variant="secondary"
                      size="xs"
                      disabled={serverSidePagination.pageIndex <= 1}
                      onClick={() => serverSidePagination.onPageIndexChange(serverSidePagination.pageIndex - 1)}
                      aria-label="Previous Page"
                      style={{ borderWidth: '1px' }}
                    >
                      <Icon icon={Icon.icons.arrowLeft} size="xxs" />
                    </Button>
                    <Text
                      size="xs"
                      component="div"
                      aria-label={`Page ${serverSidePagination.pageIndex}`}
                      className={s.pageItem}
                    >
                      {serverSidePagination.pageIndex}
                    </Text>
                    <Button
                      variant="secondary"
                      size="xs"
                      disabled={!serverSidePagination.hasNextPage}
                      onClick={() => serverSidePagination.onPageIndexChange(serverSidePagination.pageIndex + 1)}
                      aria-label="Next Page"
                      style={{ borderWidth: '1px' }}
                    >
                      <Icon icon={Icon.icons.arrowRight} size="xxs" />
                    </Button>
                  </div>
                  <SelectInput
                    className={ui.containerFormField}
                    value={
                      pageSizes.includes(serverSidePagination.pageSize)
                        ? { value: String(serverSidePagination.pageSize), label: String(serverSidePagination.pageSize) }
                        : { value: String(PAGE_SIZE), label: String(PAGE_SIZE) }
                    }
                    options={pageSizes.map((size) => ({ value: String(size), label: String(size) }))}
                    onChange={(v: TOption) => serverSidePagination.onPageSizeChange(Number(v?.value ?? PAGE_SIZE))}
                    menuPlacement="top"
                  />
                </div>
              </div>
            ) : (
              <Pagination
                total={options?.rowCount || totalItems}
                current={pageIndex}
                onPageIndexChange={(currentPage) => setPageIndex(currentPage - 1)}
                pageSize={getState().pagination.pageSize}
                pageSizes={pageSizes}
                onPageSizeChange={(v: TOption) => {
                  setPageSize(Number(v?.value));
                  setPageIndex(0);
                }}
                isDisabled={(options?.rowCount || totalItems) < PAGE_SIZE}
              />
            ))}
        </>
      )}

      {/* <pre>{JSON.stringify(table.getState(), null, 2)}</pre> */}
    </div>
  );
};
